import api, { uploadApi } from './axios';

/**
 * Get all users with pagination and search options
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.sort - Sort field and direction
 * @returns {Promise} Promise object representing the API response
 */
export const getUsers = (options = {}) => {
  const { page = 1, limit = 10, search, sort = '-createdAt' } = options;
  
  return api.get('/users', {
    params: {
      page,
      limit,
      search,
      sort,
    },
  });
};

/**
 * Get a user by ID or username
 * @param {string} idOrUsername - User ID or username
 * @returns {Promise} Promise object representing the API response
 */
export const getUserByIdOrUsername = (idOrUsername) => {
  return api.get(`/users/${idOrUsername}`);
};

/**
 * Update user profile
 * @param {Object} profileData - User profile data to update
 * @returns {Promise} Promise object representing the API response
 */
export const updateUserProfile = (profileData) => {
  return api.put('/users/profile', profileData);
};

/**
 * Update username
 * @param {string} username - New username
 * @returns {Promise} Promise object representing the API response
 */
export const updateUsername = (username) => {
  return api.put('/users/username', { username });
};

/**
 * Upload profile picture
 * @param {File} image - Image file for profile picture
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const uploadProfilePicture = (image, onUploadProgress) => {
  const formData = new FormData();
  formData.append('profileImage', image);  // Changed from 'image' to 'profileImage'
  
  return api.put('/users/profile-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
};

/**
 * Upload cover picture
 * @param {File} image - Image file for cover picture
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const uploadCoverPicture = (image, onUploadProgress) => {
  const formData = new FormData();
  formData.append('coverImage', image);  // Changed from 'image' to 'coverImage'
  
  return api.put('/users/cover-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
};

/**
 * Update user experience
 * @param {Array} experience - Array of experience objects
 * @returns {Promise} Promise object representing the API response
 */
export const updateUserExperience = (experience) => {
  return api.put('/users/experience', { experience });
};

/**
 * Update user education
 * @param {Array} education - Array of education objects
 * @returns {Promise} Promise object representing the API response
 */
export const updateUserEducation = (education) => {
  return api.put('/users/education', { education });
};

/**
 * Follow a user
 * @param {string} userId - ID of the user to follow
 * @returns {Promise} Promise object representing the API response
 */
export const followUser = (userId) => {
  return api.put(`/users/${userId}/follow`);
};

/**
 * Unfollow a user
 * @param {string} userId - ID of the user to unfollow
 * @returns {Promise} Promise object representing the API response
 */
export const unfollowUser = (userId) => {
  return api.put(`/users/${userId}/unfollow`);
};

/**
 * Get user's followers
 * @param {string} userId - User ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getUserFollowers = (userId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/users/${userId}/followers`, {
    params: {
      page,
      limit,
    },
  });
};

/**
 * Get users that the specified user is following
 * @param {string} userId - User ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getUserFollowing = (userId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/users/${userId}/following`, {
    params: {
      page,
      limit,
    },
  });
};